import {
  Button,
  DatePicker,
  Divider,
  Dropdown,
  Form,
  Input,
  Select,
  Space,
  Switch,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import moment from 'moment';
import {
  type FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';

import { useLazyGetBranchesForSystemQuery } from '@app/api/Branches';
import {
  useLazyGetPositionsForSystemQuery,
  useLazyGetPositionsQuery,
  useUpdatePositionMutation,
} from '@app/api/Positions';
import {
  useNormalizeUserPhotoMutation,
  useSetUserPhotoMutation,
  useUpdateUserMutation,
} from '@app/api/User/api';
import {
  Avatar,
  AvatarDropdownItems,
  ConfirmDeletingElement,
  FastCreationModal,
  Phone,
} from '@app/components';
import { CustomAlert } from '@app/components/CustomAlert';
import type { PhoneValidation } from '@app/components/Phone/types';
import { SetBranchesComponent } from '@app/components/SetBranchesComponent';
import {
  ADD_POSITION_LABEL,
  BRANCHES_LABEL,
  DATE_PICKER_FORMAT,
  ISO_FORMAT_INDEX,
  OFF,
  ONE_TIME_CODE,
  PASSWORD_VALIDATION_RULES,
} from '@app/constants';
import {
  EMAIL_LENGTH_VALIDATION,
  EMAIL_TYPE_VALIDATION,
  ERROR,
  ERROR_REQUIRED_FIELD,
  FRONT_WARNING,
  SUCCESS,
  WARNING,
} from '@app/constants/alerts';
import { PREFIX } from '@app/constants/appPrefix';
import { GENDER, UNKNOWN_GENDER_ID } from '@app/constants/gender';
import { rangePickerLocale } from '@app/constants/rangePickerLocale';
import {
  useMemoizeObject,
  useNewUserAccessRights,
  useUserPrint,
  useUsersAccessRights,
} from '@app/hooks';
import { LeavePageContext, NotificationContext } from '@app/providers';
import { handleCatchError } from '@app/services';
import { generateSpanWithDataTestId } from '@app/shared/utils';
import { selectActiveBranchCountryId, useAppSelector } from '@app/store';
import type {
  NBranches,
  NEmployees,
  NServices,
  NSmallGlobalEntities,
  NUsers,
  NUtils,
} from '@app/types';
import {
  afterUserUpdate,
  capitalizeKeys,
  deletePhoneDividers,
  keysFirstLetterToLower,
  transformImportedName,
} from '@app/utils';

import type { ValuesType } from '@pages/Users/EditUser/types';

import '@pages/Users/EditUser/components/GeneralInformationTab/styles/GeneralInformationTab.desktop.scss';
import '@pages/Users/EditUser/components/GeneralInformationTab/styles/GeneralInformationTab.tablet.scss';

const { Text } = Typography;
const { TextArea } = Input;
const { useForm } = Form;

export const GeneralInformationTab: FC<NUsers.GeneralInformationTab> = ({
  initUserData,
  onUserUpdate,
}) => {
  const [user, setUser] = useState<NUsers.User | null>(initUserData || null);
  const [isLoadingTab, setIsLoadingTab] = useState(false);

  const navigate = useNavigate();
  const [serverError, setServerError] = useState('');
  const [errorType, setErrorType] = useState<NUtils.ErrorTypes>(undefined);
  const [isOpenUserModal, setIsOpenUserModal] = useState(false);
  const [isPositionsModalOpen, setIsPositionsModalOpen] = useState(false);
  const [isOpenConfirmDeletingModal, setIsOpenConfirmDeletingModal] =
    useState(false);
  const [positions, setPositions] = useState<NSmallGlobalEntities.ModelItem[]>(
    []
  );
  const [allBranches, setAllBranches] = useState<NBranches.ModelBranchItem[]>(
    []
  );

  const [modalForm] = useForm();
  const [generalForm] = useForm();
  const { showNotification } = useContext(NotificationContext);

  const {
    hasUserRightToChangeOwner,
    hasUserRightToChangeBranches,
    hasUserRightToDelete,
  } = useUsersAccessRights();

  const {
    userAccessRights: { Users, IsAdmin },
  } = useNewUserAccessRights();

  const hasUserRightToCreate = useMemo(() => {
    return Users.IsCreate;
  }, [Users]);
  const activeBranchCountry = useAppSelector(selectActiveBranchCountryId);

  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [, setIsDeleted] = useState<boolean>(false);
  const [selectedBranches, setSelectedBranches] = useState<
    NBranches.ModelBranchItem[]
  >([]);
  const {
    setNewData,
    initialData,
    setInputData,
    setDatePickerData,
    setSelectorData,
    setSwitchData,
  } = useContext(LeavePageContext);

  const [phoneValidation, setPhoneValidation] = useState<PhoneValidation>({
    isValid: false,
    errorMessage: null,
  });

  const { activeBranchId, userId, database } = useUserPrint();
  const memoizedAllBranches = useMemoizeObject(allBranches);
  const memoizedUser = useMemoizeObject(user);

  const [getBranchesForSystem] = useLazyGetBranchesForSystemQuery();
  const [getPositionsForSystem] = useLazyGetPositionsForSystemQuery();
  const [getPositions] = useLazyGetPositionsQuery();
  const [updatePosition] = useUpdatePositionMutation();
  const [normalizeUserPhoto] = useNormalizeUserPhotoMutation();
  const [setUserPhoto] = useSetUserPhotoMutation();
  const [updateUser] = useUpdateUserMutation();

  const fallbackName = useMemo(() => {
    if (!user) return;
    if (user?.photo) return;

    const { nameFirst, nameLast } = user;

    const combinedName =
      nameLast && nameFirst
        ? `${nameLast} ${nameFirst}`
        : nameLast || nameFirst || '';

    return transformImportedName(combinedName);
  }, [user]);

  const photoUrl = useMemo(() => {
    if (!user?.photo) return;

    return `data:image/png;base64,${user?.photo}`;
  }, [user?.photo]);

  const uploadAvatar = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (file) {
      const reader = new FileReader();
      reader.readAsDataURL(file);

      reader.onload = async function () {
        if (typeof reader.result === 'string') {
          const base64String = reader.result
            .replace('data:', '')
            .replace(/^.+,/, '');

          if (!user?.id) {
            showNotification(
              'Не удалось определить идентификатор пользователя',
              FRONT_WARNING
            );
            return;
          }

          try {
            const { NormalizedPhoto } = await normalizeUserPhoto({
              ActiveBranchId: activeBranchId,
              data: {
                InputImage: base64String,
                InputBytes: null,
                NormalizedPhoto: null,
              },
            }).unwrap();

            const result = await setUserPhoto({
              ActiveBranchId: activeBranchId,
              data: {
                UserId: user.id,
                PhotoBase64: NormalizedPhoto,
              },
            }).unwrap();

            if (user) {
              setUser({
                ...user,
                photo: result.PhotoBase64 || '',
              });
            }

            showNotification('Фото установлено', SUCCESS);
          } catch (error) {
            showNotification(handleCatchError(error));
          }
        }
      };
    }
  };

  const deleteAvatar = async () => {
    if (!user?.id) {
      showNotification(
        'Не удалось определить идентификатор пользователя',
        FRONT_WARNING
      );
      return;
    }

    try {
      const result = await setUserPhoto({
        ActiveBranchId: activeBranchId,
        data: {
          UserId: user.id,
          PhotoBase64: null,
        },
      }).unwrap();

      if (user) {
        setUser({
          ...user,
          photo: result.PhotoBase64 || '',
        });
      }

      showNotification('Фото удалено', SUCCESS);
    } catch (error) {
      showNotification(handleCatchError(error));
    }
  };

  const avatarDropdownItems = AvatarDropdownItems({
    uploadAvatar,
    deleteAvatar,
    Photo: user?.photo || undefined,
  });

  const handleOk = useCallback(() => {
    modalForm.submit();
    setIsPositionsModalOpen(false);
  }, [modalForm]);

  const handleCancel = useCallback(() => {
    modalForm.resetFields();
    setIsPositionsModalOpen(false);
  }, [modalForm]);

  const hideUserModal = useCallback(() => {
    setIsOpenUserModal(false);
    setServerError('');
    setErrorType(undefined);
  }, []);

  const showUserModal = useCallback(() => {
    setIsOpenUserModal(true);
  }, []);

  const updateUserHandler: NServices.SendDataToDb<NUsers.ModelUser> =
    useCallback(
      async ({ data, submitType }) => {
        if (userId && database && activeBranchId) {
          try {
            const newData = await updateUser({
              data,
              ActiveBranchId: activeBranchId,
              logs: { isLogged: true, userId, database },
            }).unwrap();

            showNotification(
              'Информация о пользователе обновлена успешно',
              SUCCESS
            );

            afterUserUpdate({
              id: data.Id,
              navigate,
              submitType,
              isEditPage: true,
            });

            generalForm.resetFields(['password']);

            const updatedUser = keysFirstLetterToLower(newData) as NUsers.User;

            setUser(updatedUser);
            onUserUpdate?.(updatedUser);
          } catch (error) {
            setServerError(handleCatchError(error));
            setErrorType(ERROR);
          }
        }
      },
      [
        userId,
        database,
        activeBranchId,
        generalForm,
        navigate,
        showNotification,
        setServerError,
        setErrorType,
        setUser,
        updateUser,
        onUserUpdate,
      ]
    );

  const showModal = useCallback(() => {
    setIsPositionsModalOpen(true);
  }, []);

  const getDataHandler = useCallback(async () => {
    if (!activeBranchId) return;

    try {
      const [branches, positions] = await Promise.all([
        getBranchesForSystem({ ActiveBranchId: activeBranchId }).unwrap(),
        getPositionsForSystem({ ActiveBranchId: activeBranchId }).unwrap(),
      ]);

      setAllBranches(branches);
      setPositions([{ Id: 0, IdS: null, Name: 'Без должности' }, ...positions]);
    } catch (error) {
      showNotification(error);
    }
  }, [
    activeBranchId,
    getBranchesForSystem,
    getPositionsForSystem,
    showNotification,
  ]);

  useEffect(() => {
    getDataHandler();
  }, [getDataHandler]);

  useEffect(() => {
    memoizedUser?.isAdmin && setIsAdmin(memoizedUser?.isAdmin);
    memoizedUser?.isDeleted && setIsDeleted(memoizedUser?.isDeleted);
    setSelectedBranches(
      (memoizedAllBranches || []).filter((branch) =>
        memoizedUser?.userBranchIds?.find((id) => id === branch.Id)
      )
    );
    const birthday = memoizedUser?.dateBirth
      ? memoizedUser?.dateBirth.slice(0, 10)
      : undefined;
    generalForm.setFieldsValue({
      Phone: {
        Phone: memoizedUser?.phone.replace(/\+/g, ''),
        CountryId: memoizedUser?.phoneCountryId ?? (activeBranchCountry || 1),
      },
      firstName: memoizedUser?.nameFirst ? memoizedUser?.nameFirst : '',
      lastName: memoizedUser?.nameLast ? memoizedUser?.nameLast : '',
      middleName: memoizedUser?.nameMiddle,
      email: memoizedUser?.email,
      password: memoizedUser?.password,
      phone: memoizedUser?.phone,
      position:
        memoizedUser?.positionId === null ? 0 : memoizedUser?.positionId,
      gender: memoizedUser?.gender ?? UNKNOWN_GENDER_ID,
      isAdmin: memoizedUser?.isAdmin,
      dateBirth: birthday ? dayjs(birthday) : '',
      isDeleted: memoizedUser?.isDeleted,
      description: memoizedUser?.description,
      note: memoizedUser?.note,
      alterIsEmployee: memoizedUser?.isEmployee,
      telegramId: memoizedUser?.telegramId,
    });
  }, [memoizedAllBranches, generalForm, memoizedUser, activeBranchCountry]);

  const finishModal: NEmployees.FinishModal = useCallback(
    async ({ Name, Description }) => {
      try {
        await updatePosition({
          ActiveBranchId: activeBranchId,
          data: {
            Id: 0,
            Name,
            Description,
            IsDeleted: false,
            MigrateId: null,
          },
        }).unwrap();

        showNotification('Должность успешно добавлена', SUCCESS);

        const response = await getPositions({
          ActiveBranchId: activeBranchId,
        }).unwrap();
        const adaptedDataForUi = response.map(({ Id, Name }) => ({
          Id,
          Name,
          IdS: null,
        }));

        adaptedDataForUi.unshift({
          Id: 0,
          IdS: null,
          Name: 'Без должности',
        });

        setPositions(adaptedDataForUi);

        const selectedPosition = response.find(
          ({ Name: localName }) => localName === Name
        );

        if (selectedPosition) {
          generalForm.setFieldValue('position', selectedPosition.Id);
        }
      } catch (error) {
        if ((error as Error).cause === ERROR) {
          showNotification(error);
        } else if ((error as Error).cause === WARNING) {
          setServerError((error as Error).message);
        }
      }
    },
    [
      activeBranchId,
      generalForm,
      showNotification,
      setServerError,
      setPositions,
      getPositions,
      updatePosition,
    ]
  );

  const sendDataToDb = useCallback(
    async (values: ValuesType) => {
      const {
        lastName,
        firstName,
        middleName,
        email,
        password,
        position,
        gender,
        dateBirth,
        isAdmin,
        isDeleted,
        description,
        note,
        alterIsEmployee,
        telegramId,
        Phone: { Phone, CountryId },
      } = values;

      const { errorMessage, isValid } = phoneValidation;

      const phoneErrorMessage =
        errorMessage || (!isValid ? 'Обязательное поле' : '');

      if (phoneErrorMessage) {
        generalForm.setFields([
          {
            name: 'Phone',
            errors: [phoneErrorMessage],
          },
        ]);
        return;
      }

      setIsLoadingTab(true);

      if (!user)
        return showNotification(
          'Данные по пользователю не найдены',
          FRONT_WARNING
        );

      const data = {
        ...capitalizeKeys<NUsers.ModelUser>(user),
        NameFirst: firstName,
        NameMiddle: middleName,
        NameLast: lastName,
        Email: email,
        Password: password,
        Phone: Phone,
        PhoneCode: null,
        PhoneCountryId: CountryId,
        PositionId: position === 0 ? null : position,
        Gender:
          gender === UNKNOWN_GENDER_ID || gender === undefined ? null : gender,
        DateBirth: dateBirth
          ? dateBirth.format(DATE_PICKER_FORMAT[ISO_FORMAT_INDEX])
          : null,
        IsAdmin: isAdmin,
        UserBranchIds: selectedBranches.length
          ? selectedBranches.map(({ Id }) => Id)
          : null,
        IsDeleted: isDeleted,
        Description: description,
        Note: note,
        TelegramId: telegramId || null,
        IsSaveAdditions: false,
        IsFakePhone: initialData?.isFakePhone
          ? deletePhoneDividers(
              generalForm.getFieldValue('phone').label +
                generalForm.getFieldValue('phone')
            ) === initialData.phone
          : false,
        IsFakeEmail: initialData?.isFakeEmail
          ? email === initialData.email
          : false,
        AlterIsEmployee: Boolean(alterIsEmployee),
      };

      try {
        await updateUserHandler({
          data,
          submitType: 'addUser',
        });
      } catch (error) {
        setServerError(error as string);
      } finally {
        setIsLoadingTab(false);
      }
    },
    [
      user,
      selectedBranches,
      initialData?.isFakePhone,
      initialData.phone,
      initialData?.isFakeEmail,
      initialData.email,
      generalForm,
      updateUserHandler,
      showNotification,
      phoneValidation,
    ]
  );

  const deleteEmployee = async () => {
    setIsLoadingTab(true);

    if (!user)
      return showNotification(
        'Данные по пользователю не найдены',
        FRONT_WARNING
      );

    const birthDate = generalForm.getFieldValue('dateBirth');
    const data = {
      ...capitalizeKeys<NUsers.ModelUser>(user),
      NameFirst: generalForm.getFieldValue('firstName'),
      NameMiddle: generalForm.getFieldValue('middleName'),
      NameLast: generalForm.getFieldValue('lastName'),
      Email:
        user?.isFakeEmail && !generalForm.getFieldValue('email')
          ? null
          : generalForm.getFieldValue('email'),
      Password: generalForm.getFieldValue('password'),
      Phone:
        user?.isFakePhone && !generalForm.getFieldValue('phone')
          ? null
          : generalForm.getFieldValue('phone'),
      PositionId:
        generalForm.getFieldValue('position') === 0
          ? null
          : generalForm.getFieldValue('position'),
      Gender:
        generalForm.getFieldValue('gender') === '' ||
        generalForm.getFieldValue('gender') === undefined
          ? null
          : generalForm.getFieldValue('gender'),
      DateBirth: birthDate
        ? birthDate.format(DATE_PICKER_FORMAT[ISO_FORMAT_INDEX])
        : null,
      IsAdmin: generalForm.getFieldValue('isAdmin'),
      UserBranchIds: selectedBranches.length
        ? selectedBranches.map(({ Id }) => Id)
        : null,
      IsDeleted: true,
      Description: generalForm.getFieldValue('description'),
      Note: generalForm.getFieldValue('note'),
      IsSaveAdditions: false,
    };

    await updateUserHandler({
      data,
      submitType: 'addUser',
    })
      .catch((error: string) => setServerError(error))
      .finally(() => {
        setIsLoadingTab(false);
        setIsOpenConfirmDeletingModal(false);
      });
  };

  return (
    <>
      <Form
        form={generalForm}
        name="basic"
        style={{ maxWidth: '800px', width: '100%' }}
        initialValues={{
          remember: true,
        }}
        autoComplete={OFF}
        className={`${PREFIX}edit-user` + '-general-information' + ' bc-form'}
        onFinish={sendDataToDb}
      >
        <Space
          direction="vertical"
          size="middle"
          className="form-space"
          style={{ width: '100%' }}
        >
          {IsAdmin && (
            <div className={'container'}>
              <Typography.Text className="label" strong data-testid="surname">
                Фото
              </Typography.Text>
              <div className="employee_avatar second-column">
                <Avatar src={photoUrl} nameFirst={fallbackName} />
                <Dropdown menu={{ items: avatarDropdownItems }}>
                  <Button
                    data-testid="change-avatar"
                    type="link"
                    disabled={isLoadingTab}
                  >
                    Изменить
                  </Button>
                </Dropdown>
              </div>
            </div>
          )}
          <div className="container">
            <Text strong className="label label-require" data-testid="name">
              Имя
            </Text>
            <Form.Item
              name="firstName"
              rules={[
                {
                  required: true,
                  message: generateSpanWithDataTestId(
                    ERROR_REQUIRED_FIELD,
                    'alert'
                  ),
                },
              ]}
              className="mg-0 second-column"
            >
              <Input
                data-testid="name-input"
                autoComplete={ONE_TIME_CODE}
                size="large"
                maxLength={60}
                onChange={(e) => {
                  setInputData({
                    event: e,
                    fieldName: 'nameFirst',
                    isEditPage: true,
                    isTextField: true,
                  });
                }}
              />
            </Form.Item>
          </div>
          <div className="container">
            <Text strong className="label" data-testid="surname">
              Фамилия
            </Text>
            <Form.Item name="lastName" className="mg-0 second-column">
              <Input
                data-testid="surname-input"
                autoComplete={ONE_TIME_CODE}
                size="large"
                maxLength={60}
                onChange={(e) => {
                  setInputData({
                    event: e,
                    fieldName: 'nameLast',
                    isEditPage: true,
                    isTextField: true,
                  });
                }}
              />
            </Form.Item>
          </div>
          <div className="container">
            <Text strong className="label" data-testid="patronymic">
              Отчество
            </Text>
            <Form.Item name="middleName" className="mg-0 second-column">
              <Input
                data-testid="patronymic-input"
                autoComplete={ONE_TIME_CODE}
                size="large"
                maxLength={80}
                onChange={(e) => {
                  setInputData({
                    event: e,
                    fieldName: 'nameMiddle',
                    isEditPage: true,
                    isTextField: true,
                  });
                }}
              />
            </Form.Item>
          </div>
          <div className="container">
            <Text strong className="label label-require" data-testid="email">
              Email
            </Text>
            <Form.Item
              name="email"
              className="mg-0 second-column"
              rules={[
                {
                  required: true,
                  message: generateSpanWithDataTestId(
                    ERROR_REQUIRED_FIELD,
                    'alert'
                  ),
                },
                EMAIL_TYPE_VALIDATION,
                EMAIL_LENGTH_VALIDATION,
              ]}
            >
              <Input
                data-testid="email-input"
                maxLength={100}
                autoComplete={ONE_TIME_CODE}
                size="large"
                onChange={(e) => {
                  setInputData({
                    event: e,
                    fieldName: 'email',
                    isEditPage: true,
                    isTextField: true,
                  });
                }}
              />
            </Form.Item>
          </div>
          <div className="container">
            <Text strong className="label" data-testid="password">
              Пароль
            </Text>
            <Form.Item
              name="password"
              rules={[...PASSWORD_VALIDATION_RULES]}
              className="mg-0 second-column"
            >
              <Input.Password
                data-testid="new-password-input"
                autoComplete={ONE_TIME_CODE}
                minLength={6}
                maxLength={60}
                size="large"
                onChange={(e) => {
                  setInputData({
                    event: e,
                    fieldName: 'password',
                    isEditPage: false,
                    isTextField: true,
                  });
                }}
              />
            </Form.Item>
          </div>
          <div className="container">
            <Text strong className="label" data-testid="phone">
              Мобильный телефон
            </Text>
            <Form.Item name="Phone" className="mg-0 second-column">
              <Phone
                name="Phone"
                required
                onValidate={setPhoneValidation}
                phoneInputDataTestId="phone-input"
              />
            </Form.Item>
          </div>
          <div className="container">
            <Text strong className="label" data-testid="telegram">
              Telegram
            </Text>
            <Form.Item name="telegramId" className="mg-0 second-column">
              <Input
                data-testid="telegram-input"
                autoComplete={ONE_TIME_CODE}
                size="large"
                maxLength={100}
                onChange={(e) => {
                  setInputData({
                    event: e,
                    fieldName: 'telegramId',
                    isEditPage: true,
                    isTextField: true,
                  });
                }}
              />
            </Form.Item>
          </div>
          <div className="container">
            <Typography.Text className="label" strong data-testid="position">
              Должность
            </Typography.Text>

            <Form.Item name="position" className="mg-0 second-column">
              <Select
                data-testid="position-select"
                size="large"
                showSearch
                optionFilterProp="label"
                options={positions.map((position) => {
                  return {
                    label: position.Name,
                    value: position.Id,
                  };
                })}
                popupRender={(menu) => (
                  <>
                    {hasUserRightToCreate ? (
                      <>
                        <Button type="link" size="small" onClick={showModal}>
                          {ADD_POSITION_LABEL}
                        </Button>
                        <Divider style={{ margin: '8px 0' }} />
                        {menu}
                      </>
                    ) : (
                      <>{menu}</>
                    )}
                  </>
                )}
                onChange={(value) => {
                  setSelectorData({ value, fieldName: 'position' });
                }}
              />
            </Form.Item>
          </div>
          <div className="container">
            <Text strong className="label" data-testid="gender">
              Пол
            </Text>
            <Form.Item name="gender" className="mg-0 second-column">
              <Select
                data-testid="gender-select"
                className="field-selector"
                size="large"
                options={GENDER}
                onChange={(value) => {
                  generalForm.setFieldsValue({ gender: value });
                  setSelectorData({ value, fieldName: 'gender' });
                }}
              />
            </Form.Item>
          </div>
          <div className="container">
            <Text strong className="field date-label" data-testid="birth-date">
              Дата рождения
            </Text>
            <Form.Item
              name="dateBirth"
              className="mg-0 date-picker second-column"
            >
              <DatePicker
                data-testid="birth-date-select"
                autoComplete={ONE_TIME_CODE}
                locale={rangePickerLocale}
                size="large"
                format={DATE_PICKER_FORMAT}
                onChange={(date) => {
                  setDatePickerData({
                    dateString: date,
                    fieldName: 'dateBirth',
                  });
                }}
                disabledDate={(current) =>
                  current.unix() >= moment().startOf('day').unix()
                }
              />
            </Form.Item>
          </div>
          <div
            className="container"
            style={{ alignItems: 'flex-start' }}
            data-testid="description"
          >
            <Text strong className="label">
              Описание
            </Text>

            <Form.Item name="description" className="mg-0 second-column">
              <TextArea
                data-testid="description-input"
                autoComplete={ONE_TIME_CODE}
                rows={4}
                maxLength={260}
                onChange={(e) => {
                  setInputData({
                    event: e,
                    fieldName: 'description',
                    isEditPage: true,
                    isTextField: true,
                  });
                }}
              />
            </Form.Item>
          </div>
          <div
            className="container description-container"
            style={{ alignItems: 'flex-start' }}
            data-testid="note"
          >
            <Text strong className="label">
              Примечание
            </Text>

            <Form.Item name="note" className="mg-0 second-column">
              <TextArea
                data-testid="description-input"
                autoComplete={ONE_TIME_CODE}
                rows={4}
                maxLength={260}
                onChange={(e) => {
                  setInputData({
                    event: e,
                    fieldName: 'description',
                    isEditPage: true,
                    isTextField: true,
                  });
                }}
              />
            </Form.Item>
          </div>
          <Divider className="divider" />
          <div
            style={{
              display: hasUserRightToChangeOwner ? 'flex' : 'none',
              marginBottom: '15px',
            }}
          >
            <Form.Item name="isAdmin" valuePropName="checked" className="mg-0">
              <Switch
                data-testid={
                  generalForm.getFieldValue('isAdmin')
                    ? 'switcher-on'
                    : 'switcher-off'
                }
                onChange={(status) => {
                  setIsAdmin(!isAdmin);
                  setSwitchData({
                    value: status,
                    fieldName: 'isAdmin',
                  });
                }}
              />
            </Form.Item>
            <div style={{ marginLeft: '10px' }}>
              <Text strong className="label" data-testid="owner">
                Владелец <br />
              </Text>
              <Text type="secondary" data-testid="owner-description">
                Пользователь с наивысшими правами
              </Text>
            </div>
          </div>
          <div style={{ display: 'flex', marginBottom: '15px' }}>
            <Form.Item
              name="alterIsEmployee"
              valuePropName="checked"
              className="mg-0"
            >
              <Switch
                data-testid={
                  generalForm.getFieldValue('alterIsEmployee')
                    ? 'switcher-on'
                    : 'switcher-off'
                }
                onChange={(value) => {
                  setSwitchData({
                    value,
                    fieldName: 'alterIsEmployee',
                  });
                }}
              />
            </Form.Item>
            <div style={{ marginLeft: '10px' }}>
              <Text strong className="label" data-testid="executor">
                Исполнитель <br />
              </Text>
              <Text type="secondary" data-testid="executor-description">
                Является ли пользователь также исполнителем
              </Text>
            </div>
          </div>
          <div
            className="branches-container"
            style={{ display: hasUserRightToChangeBranches ? 'block' : 'none' }}
          >
            <Typography.Title level={3} className="mg-0" data-testid="branches">
              {BRANCHES_LABEL}
            </Typography.Title>
            <Text type="secondary" data-testid="branches-hint">
              Укажите, в каких филиалах может работать пользователь
            </Text>
            <SetBranchesComponent
              open={isOpenUserModal}
              onCancel={hideUserModal}
              selectedBranches={selectedBranches}
              setSelectedBranches={setSelectedBranches}
              allBranches={allBranches}
              showUserModal={showUserModal}
              tooltipLabel="Укажите, в каких филиалах может работать пользователь"
              setNewData={setNewData}
              newDataFieldValue="userBranchIds"
            />
          </div>
          <Space
            direction="vertical"
            size="large"
            className="mg-0 button-space"
          >
            {serverError && errorType && (
              <CustomAlert
                errorType={errorType}
                serverError={serverError}
                setServerError={setServerError}
              />
            )}

            <div style={{ display: 'flex', gap: '16px' }}>
              <Button
                className="save-button"
                size="large"
                type="primary"
                htmlType="submit"
                loading={isLoadingTab}
                data-testid="save-edit-user"
              >
                Сохранить
              </Button>

              <Button
                danger
                className="save-button"
                disabled={isLoadingTab}
                size="large"
                onClick={() => setIsOpenConfirmDeletingModal(true)}
                style={{ display: hasUserRightToDelete ? 'block' : 'none' }}
                data-testid="delete-user"
              >
                Удалить
              </Button>
            </div>
          </Space>
        </Space>
      </Form>
      <ConfirmDeletingElement
        modalOpen={isOpenConfirmDeletingModal}
        setModalOpen={setIsOpenConfirmDeletingModal}
        handleDelete={deleteEmployee}
        content="Вы уверены, что хотите удалить пользователя?"
        title="Удаление пользователя"
        modalDataTestId="delete-user-popup"
        titleDataTestId="delete-title"
        descriptionDataTestId="delete-description"
      />
      <FastCreationModal
        title="Создание должности"
        isModalOpen={isPositionsModalOpen}
        handleOk={handleOk}
        handleCancel={handleCancel}
        modalForm={modalForm}
        finishModal={finishModal}
        nameMaxLength={40}
        descriptionMaxLength={260}
      />
    </>
  );
};
